// @ts-check
import { Image } from '@react-pdf/renderer';
import React from 'react';
import BookmarkIcon from '../../components/BookmarkIcon';
import {
  Box,
  Company,
  Flex,
  Product,
  QRCode,
  Registration,
  Text,
  Title,
} from '../../pdf-design-system';
import roussillonLogo_small from './images/roussillonLogo_small.jpeg';
import { theme } from './RoussillonCatalog';
import { PageLayout } from './RoussillonCatalog';

export default function RoussillonCatalogWinePage({
  bleed = 3,
  page,
  theme,
  baseUrl,
}) {
  const { registration } = page;
  return (
    <PageLayout theme={theme} bleed={bleed}>
      <PageBody bleed={bleed}>
        <ExhibitorHeader registration={registration} />
        <Box key={registration.registrationId} style={{}}>
          <Registration.Provider value={registration}>
            <Flex
              direction="column"
              style={{ borderBottom: 1, borderColor: '#CCC' }}
            >
              {registration.registeredProducts?.nodes?.map(
                (registeredProduct, index) => (
                  <ProductRow key={index}>
                    <Flex style={{ marginTop: '3mm' }}>
                      <Product.BottleImage
                        height={150}
                        width={60}
                        product={registeredProduct.product}
                        style={{ marginRight: '3mm' }}
                      />
                      <Flex
                        direction="column"
                        style={{
                          justifyContent: 'space-between',
                          width: '100%',
                        }}
                      >
                        <Box>
                          <Flex
                            style={{
                              alignItems: 'flex-start',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Flex>
                              <ProductName
                                registeredProduct={registeredProduct}
                              />
                            </Flex>
                          </Flex>
                          <Product.Blend.Value
                            product={registeredProduct.product}
                            style={{ color: '#888', width: '100%' }}
                          />
                          <Text variant="light">
                            {registeredProduct.product.denomination}
                          </Text>
                          <Analytics registeredProduct={registeredProduct} />
                          <Price registeredProduct={registeredProduct} />
                        </Box>
                        <Flex style={{ alignItems: 'flex-end' }}>
                          <TastingNote registeredProduct={registeredProduct} />
                          <QRCode
                            url={`${baseUrl}/e/${registration.companyId}`}
                            errorCorrectionLevel="L"
                            margin={0}
                            style={{
                              width: '15mm',
                              height: '15mm',
                              flexShrink: 0,
                              marginTop: 10,
                              marginBottom: 3,
                              marginRight: 0,
                              marginLeft: '20mm',
                            }}
                          />
                        </Flex>
                      </Flex>
                      <Bookmark registeredProduct={registeredProduct} />
                    </Flex>
                  </ProductRow>
                )
              )}
            </Flex>
          </Registration.Provider>
        </Box>
      </PageBody>

      <Flex
        style={{
          position: 'absolute',
          // backgroundColor: theme.colors.accent,
          paddingHorizontal: `${15 + bleed * 2}mm`,
          bottom: 0,
          left: 0,
          right: 0,
          height: `${20 + bleed}mm`,
          paddingBottom: `${bleed}mm`,
          alignItems: 'center',
        }}
      >
        <Image src={roussillonLogo_small} style={{ marginRight: 3 }} />
        <Box>
          <Text variant="light">View our online tasting catalog at </Text>
          <Text variant="base">{baseUrl}</Text>
        </Box>
      </Flex>
    </PageLayout>
  );
}

function TastingNote({ registeredProduct }) {
  const { tastingNote } = registeredProduct;
  // We need to always return the formatted box because the layout assumes this place has something.
  // if (!tastingNote) return null;
  return (
    <Box
      style={{
        width: '7cm',
        marginBottom: '1.5mm',
      }}
    >
      {tastingNote?.rating && (
        <Text variant="light">My Rating: {tastingNote?.rating}1/5</Text>
      )}
      <Text variant="light">{tastingNote?.note}</Text>
    </Box>
  );
}

function ProductRow({ children }) {
  return (
    <Box
      style={{
        height: '50mm',
        flexShrink: 0,
        borderTop: 1,
        borderColor: '#CCC',
      }}
    >
      {children}
    </Box>
  );
}

function PageBody({ bleed, children }) {
  return (
    <Box
      style={{
        paddingHorizontal: `${15 + bleed}mm`,
        paddingTop: `${13 + bleed}mm`,
        paddingBottom: '10mm',
        // height: '216mm',
        position: 'relative',
      }}
    >
      {children}
    </Box>
  );
}

/**
 * @param {number} number
 */
function formatPrice(number) {
  if (number == null) return '';
  return number.toFixed(2).replace(',', '.');
}

/**
 * @param {number} number
 */
function formatNumber(number) {
  return number;
  if (number == null) return '';
  return number.toFixed(1).replace('.', ',');
}

function ExhibitorHeader({ registration }) {
  return (
    <Flex style={{ justifyContent: 'space-between' }}>
      <Box style={{ width: '50%' }}>
        <Company.ProfileLogo
          companyProfile={registration.profile}
          height={50}
          width={100}
          gravity={'north_west'}
        />
      </Box>
      <Flex
        style={{
          flexDirection: 'column',
          alignItems: 'flex-end',
          marginBottom: '2mm',
          width: '50%',
        }}
      >
        <Text>{registration?.stand?.name || 'Table 34'}</Text>
        <Title
          style={{
            textAlign: 'right',
            marginBottom: 5,
          }}
        >
          {registration.profile.name}
        </Title>
        <Text variant="light">
          {registration?.profile?.website?.replace(/\/$/, '')}
        </Text>
        <Text variant="light">{registration?.profile?.email}</Text>
      </Flex>
    </Flex>
  );
}

function ProductName({ registeredProduct }) {
  return (
    <Text style={{ marginRight: '1mm' }}>
      {registeredProduct.product?.name}
    </Text>
  );
}

function Analytics({ registeredProduct, variant = 'light' }) {
  const { product } = registeredProduct;
  const analyticsString = [
    product.designation,
    product.alcohol && `Alc. ${formatNumber(product.alcohol)}%`,
    product.residualSugar && `RS ${formatNumber(product.residualSugar)} g/l`,
    product.totalAcidity && `TA ${formatNumber(product.totalAcidity)} g/l`,
  ]
    .filter(Boolean)
    .join(' | ');
  return <Text variant={variant}>{analyticsString}</Text>;
}

function Price({ registeredProduct, variant = 'light' }) {
  if (registeredProduct.pricing?.price !== null)
    return (
      <Text variant={variant}>
        {registeredProduct.pricing?.currency}{' '}
        {formatPrice(registeredProduct.pricing?.price)}{' '}
        {registeredProduct.pricing?.priceType}
      </Text>
    );
  return <Text variant={variant}>Enquire for price</Text>;
}

function Bookmark({ registeredProduct }) {
  const { bookmark } = registeredProduct;
  if (!bookmark) return null;
  return (
    <Box
      style={{
        marginBottom: 6,
        marginLeft: -35,
        position: 'absolute',
        top: -7,
      }}
    >
      <BookmarkIcon
        variant="filled"
        color={theme.colors.primary}
        size="medium"
      />
    </Box>
  );
}
